import type React from 'react';
import { cn } from '../../utils/cn.js';

export interface ItemListItem {
  id: string;
  /** Leading avatar or icon tile. */
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Trailing value ("$1,200", "12 runs"). */
  value?: React.ReactNode;
  /** Under the value: a delta or caption. Colour it with `trendTone`. */
  trend?: React.ReactNode;
  trendTone?: 'positive' | 'negative' | 'neutral';
  /** The row opens something: its leading and title become one button (the trailing value stays its own control). */
  onPress?: () => void;
  /** The button's accessible name when the row is pressable (the title is the default). */
  pressLabel?: string;
}

interface ItemListProps {
  items: readonly ItemListItem[];
  /** Names the list for screen readers when no heading does. */
  'aria-label'?: string;
  className?: string;
}

const TREND_TONE = {
  positive: 'text-success-500',
  negative: 'text-error-500',
  neutral: 'text-text-tertiary',
} as const;

/**
 * A divided list of rows: leading avatar, title with a subtitle, a value with its trend on the right. Template
 * example: NextAdmin `(dashboard)/(home)/_component/top-products` (rows `border-b border-border-primary py-3 first:pt-0
 * last:border-0 last:pb-0`). Put it inside a `Card`.
 */
export function ItemList({ items, className, ...aria }: ItemListProps) {
  return (
    <ul className={cn('flex flex-col', className)} aria-label={aria['aria-label']}>
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-center justify-between gap-3 border-b border-border-primary py-3 first:pt-0 last:border-0 last:pb-0"
        >
          {item.onPress ? (
            <button
              type="button"
              onClick={item.onPress}
              aria-label={item.pressLabel}
              className="-mx-1.5 flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-background-gray-primary focus-visible:outline-2 focus-visible:outline-primary-500"
            >
              <RowBody item={item} />
            </button>
          ) : (
            <div className="flex min-w-0 items-center gap-3">
              <RowBody item={item} />
            </div>
          )}

          {(item.value !== undefined || item.trend !== undefined) && (
            <div className="shrink-0 text-right">
              {item.value !== undefined && (
                <p className="text-sm leading-5 font-medium text-text-primary">{item.value}</p>
              )}
              {item.trend !== undefined && (
                <p
                  className={cn(
                    'mt-1 flex items-center justify-end gap-1 text-xs leading-4',
                    TREND_TONE[item.trendTone ?? 'neutral'],
                  )}
                >
                  {item.trend}
                </p>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function RowBody({ item }: { item: ItemListItem }) {
  return (
    <>
      {item.leading}
      <div className="min-w-0">
        <p className="mb-1 truncate text-sm leading-5 font-medium text-text-primary">{item.title}</p>
        {item.subtitle && (
          <small className="block truncate text-xs leading-4 text-text-tertiary">{item.subtitle}</small>
        )}
      </div>
    </>
  );
}
